/**
 * Search drops down from the header's search bar (desktop / laptop), and
 * Enter with no matches keeps the shopper in the pop-up (end of file).
 *
 * Horizon opens predictive search as a dialog centred in the viewport, away
 * from the field the shopper clicked. Amazon, Noon and Flipkart keep the
 * shopper's eye where they clicked: the field stays in place and results
 * open straight below it. This module measures the header search pill when
 * it is clicked and hands its position to CSS (custom-header.css §6) as
 * custom properties on Horizon's own <dialog>, so the dialog's input row
 * sits exactly over the pill and the results drop down from it.
 *
 * Phones (< 990px) keep Horizon's full-screen search (standard on mobile).
 * Only the position changes — searching, results and keyboard handling are
 * Horizon's. Recomputed on resize while open (the sticky header can move).
 */

const DESKTOP = window.matchMedia('(min-width: 990px)');
const MIN_WIDTH = 760;
const EDGE = 24;

const PILL = '#header-component search-button .header-actions__action';
const dialog = () => document.querySelector('#search-modal .search-modal__content');

/**
 * The pill that was clicked — Horizon renders a second, hidden search button
 * for its menu drawer, so "the first one in the header" can be invisible.
 * @type {HTMLElement | null}
 */
let lastPill = null;

/** @param {HTMLElement | null} [field] */
function anchor(field = lastPill) {
  const content = dialog();
  if (!(field instanceof HTMLElement) || !(content instanceof HTMLElement)) return;

  if (!DESKTOP.matches) {
    content.removeAttribute('data-q-anchored');
    return;
  }

  const rect = field.getBoundingClientRect();
  if (!rect.width) return;

  const viewport = document.documentElement.clientWidth;
  const width = Math.min(Math.max(rect.width, MIN_WIDTH), viewport - EDGE * 2);
  // Start where the pill starts (its inline-start edge), but stay on screen
  const rtl = getComputedStyle(field).direction === 'rtl';
  let left = rtl ? rect.right - width : rect.left;
  left = Math.min(Math.max(left, EDGE), viewport - EDGE - width);

  content.style.setProperty('--q-search-top', `${Math.round(rect.top)}px`);
  content.style.setProperty('--q-search-left', `${Math.round(left)}px`);
  content.style.setProperty('--q-search-width', `${Math.round(width)}px`);
  content.style.setProperty('--q-search-field-height', `${Math.round(rect.height)}px`);
  content.setAttribute('data-q-anchored', '');
}

// Measure before Horizon opens the dialog (capture runs first)
document.addEventListener(
  'click',
  (event) => {
    const field = event.target instanceof Element ? event.target.closest(PILL) : null;
    if (!(field instanceof HTMLElement)) return;
    lastPill = field;
    anchor(field);
  },
  true
);

window.addEventListener('resize', () => {
  if (dialog()?.hasAttribute('open')) anchor();
});
DESKTOP.addEventListener('change', () => anchor());

/**
 * Enter with no matches stays in the search pop-up (all screen sizes).
 * A results page that only says "No matches" is a wasted page load, so:
 *   • the pop-up already answered for exactly what is typed:
 *       no results → stay (text selected, a short shake on the message);
 *       results    → submit as normal (the full results page);
 *   • the answer is still loading (Enter pressed quickly): wait for it
 *     (the field shows a busy state), up to WAIT_MS, then apply the same
 *     rule; no answer in time → submit.
 * The pop-up's own text says which query it answered (`"…"`), so a stale
 * answer for earlier text never decides.
 */
const WAIT_MS = 3000;

/** @param {Element} modal @param {string} query */
function answerFor(modal, query) {
  const needle = `"${query}"`;
  const none = modal.querySelector('.predictive-search-results__no-results');
  if (none?.getClientRects().length && none.textContent?.toLowerCase().includes(needle)) return 'none';
  // With results, the pop-up announces `N search results found for "…"`;
  // the typed text alone also appears in a "Search for …" row, so match the
  // announcement, not just the quoted text
  const text = modal.textContent?.toLowerCase() ?? '';
  if (text.includes(`found for ${needle}`) && !text.includes(`no results found for ${needle}`)) return 'found';
  return null;
}

/** @param {HTMLInputElement | null} input @param {Element | null} message */
function stay(input, message) {
  input?.focus();
  input?.select();
  if (!message) return;
  message.classList.remove('q-search-nudge');
  void message.getBoundingClientRect(); // restart the animation on repeated Enter
  message.classList.add('q-search-nudge');
}

/**
 * One decision for both ways a search is sent: the form submit (search
 * button) and Enter, which Horizon's predictive-search.js turns straight
 * into a navigation while it is still showing earlier suggestions.
 * @param {Element} modal
 * @param {HTMLInputElement} input
 * @param {() => void} go - continue to the results page
 */
function decide(modal, input, go) {
  const query = input.value.trim().toLowerCase();
  const answer = answerFor(modal, query);
  if (answer === 'found') return go();
  if (answer === 'none') return stay(input, modal.querySelector('.predictive-search-results__no-results'));

  // Still loading: decide when the pop-up answers for this text
  const started = performance.now();
  input.setAttribute('aria-busy', 'true');
  const done = () => input.removeAttribute('aria-busy');
  const check = () => {
    if (input.value.trim().toLowerCase() !== query) return done(); // shopper kept typing
    const later = answerFor(modal, query);
    if (later === 'none') {
      done();
      stay(input, modal.querySelector('.predictive-search-results__no-results'));
    } else if (later === 'found' || performance.now() - started > WAIT_MS) {
      done();
      go();
    } else {
      setTimeout(check, 120);
    }
  };
  setTimeout(check, 120);
}

/** @param {Element} modal @param {HTMLInputElement} input */
function goToResults(modal, input) {
  const form = /** @type {HTMLFormElement | null} */ (input.closest('form'));
  if (form) {
    form.dataset.qSubmitting = 'true';
    form.requestSubmit();
    delete form.dataset.qSubmitting;
  } else {
    const url = new URL(window.Theme?.routes?.search_url ?? '/search', location.origin);
    url.searchParams.set('q', input.value);
    location.href = url.toString();
  }
}

/**
 * Enter in the search field. Horizon's declarative events listen on window
 * (capture) and stop the event, so a listener here would run too late; the
 * page head registers a tiny window listener first (snippets/custom-globals
 * .liquid) that calls this function.
 * @param {KeyboardEvent} event
 */
window.qSearchEnter = (event) => {
  if (event.key !== 'Enter' || event.isComposing) return;
  const input = event.target;
  if (!(input instanceof HTMLInputElement)) return;
  const modal = input.closest('#search-modal');
  if (!modal || !input.value.trim()) return;
  // An arrow-key highlighted suggestion: Enter opens that item (Horizon)
  if (modal.querySelector('[aria-selected="true"]')) return;
  // Results already shown: Horizon decides (it opens a single match directly)
  if (answerFor(modal, input.value.trim().toLowerCase()) === 'found') return;

  event.preventDefault();
  event.stopImmediatePropagation();
  decide(modal, input, () => goToResults(modal, input));
};

// The search button (or any other submit)
document.addEventListener(
  'submit',
  (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || form.dataset.qSubmitting) return;
    const modal = form.closest('#search-modal');
    const input = /** @type {HTMLInputElement | null} */ (form.querySelector('input[type="search"], input[name="q"]'));
    if (!modal || !input?.value.trim()) return;
    if (answerFor(modal, input.value.trim().toLowerCase()) === 'found') return;
    event.preventDefault();
    event.stopImmediatePropagation();
    decide(modal, input, () => goToResults(modal, input));
  },
  true
);
